<script setup lang="ts">
	import { BellIcon } from '@heroicons/vue/24/outline'
	import { computed } from 'vue'

	import LoadingModal from '@/components/LoadingModal.vue'

	import EditUserNotifications from '@/modules/users/components/EditUserNotifications.vue'
	import useUserEmails from '@/modules/users/composables/useUserEmails'
	import useUserPhones from '@/modules/users/composables/useUserPhones'
	import { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()

	const { usersPhones, isLoading: loadingPhones } = useUserPhones({
		params: {
			userId: props.user.id,
		},
		immediate: true,
	})

	const { userEmails, isLoading: loadingEmails } = useUserEmails({
		params: {
			userId: props.user.id,
		},
		immediate: true,
	})

	// Verificar si hay canales disponibles para configurar
	const hasAvailableChannels = computed(() => {
		// Siempre hay al menos el email principal del usuario
		const hasMainEmail = !!props.user.email
		const hasAdditionalEmails = (userEmails.value?.length || 0) > 0
		const hasPhones = (usersPhones.value?.length || 0) > 0

		return hasMainEmail || hasAdditionalEmails || hasPhones
	})
</script>

<template>
	<div class="space-y-6">
		<!-- Componente principal de configuración -->
		<div v-if="hasAvailableChannels">
			<EditUserNotifications
				v-if="!loadingEmails && !loadingPhones"
				title="Configuración de Notificaciones"
				:settings="user.notificationSettings"
				:phones="usersPhones"
				:emails="userEmails"
				:main-email="user.email"
				:user-id="user.id"
			/>

			<!-- Estado de carga -->
			<div
				v-else
				class="flex items-center justify-center py-12 bg-white rounded-lg shadow-sm border border-gray-200"
			>
				<div class="flex items-center space-x-3">
					<div
						class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"
					></div>
					<span class="text-gray-600">Cargando configuración...</span>
				</div>
			</div>
		</div>

		<!-- Estado vacío cuando no hay canales disponibles -->
		<div
			v-if="!loadingEmails && !loadingPhones && !hasAvailableChannels"
			class="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200"
		>
			<div
				class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4"
			>
				<BellIcon class="w-8 h-8 text-gray-400" />
			</div>
			<h3 class="text-lg font-medium text-gray-900 mb-2">
				No hay canales de notificación disponibles
			</h3>
			<p class="text-gray-600 mb-4">
				Agrega un correo electrónico o número de teléfono para habilitar las
				notificaciones.
			</p>
		</div>
	</div>

	<LoadingModal :when="loadingEmails || loadingPhones" />
</template>

<style scoped></style>
